import { useEffect, useState } from "react";
import { CATEGORIES } from "@/lib/constants";
import { EnvelopeSimple, PaperPlaneTilt, SpinnerGap, TextB, TextItalic, TextUnderline } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const MAIL_SECTIONS = [
  {
    key: "otp",
    title: "Code de validation",
    hint: "Envoyé au résident pour valider son signalement.",
    vars: ["code", "copropriete"],
    defaultSubject: "{code} est votre code de validation",
  },
  {
    key: "manager",
    title: "Nouveau signalement",
    hint: "Envoyé au gestionnaire (et au concierge en copie) à chaque nouveau signalement.",
    vars: ["ref", "categorie", "lieu", "batiment", "appareil", "panne", "urgence", "gestionnaire", "gestionnaire_nom", "gestionnaire_tel", "gestionnaire_email", "description", "resident", "telephone", "email", "lien", "copropriete", "adresse"],
    defaultSubject: "Nouveau signalement {ref} - {copropriete}",
  },
  {
    key: "provider",
    title: "Demande d'intervention",
    hint: "Envoyé au prestataire concerné (gestionnaire en copie, concierge en copie cachée).",
    vars: ["prestataire", "ref", "categorie", "lieu", "batiment", "appareil", "panne", "urgence", "gestionnaire", "gestionnaire_nom", "gestionnaire_tel", "gestionnaire_email", "description", "resident", "telephone", "email", "lien", "copropriete", "adresse"],
    defaultSubject: "Demande d'intervention",
  },
  {
    key: "status",
    title: "Changement de statut",
    hint: "Envoyé au résident quand son signalement change de statut.",
    vars: ["ref", "categorie", "statut", "commentaire", "gestionnaire", "gestionnaire_nom", "gestionnaire_tel", "gestionnaire_email", "resident", "lien", "copropriete", "adresse"],
    defaultSubject: "Votre signalement {ref} : {statut}",
  },
];

const inputCls =
  "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

export default function AdminEmails() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setSettings(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const setMailTpl = (key, field, val) =>
    setSettings((s) => {
      const tpls = { ...(s.mail_templates || {}) };
      tpls[key] = { ...(tpls[key] || { subject: "", body: "" }), [field]: val };
      return { ...s, mail_templates: tpls };
    });

  const wrapSelection = (key, marker, closeMarker = null) => {
    const el = document.getElementById(`mail-${key}-body`);
    if (!el) return;
    const tpl = (settings.mail_templates || {})[key] || { subject: "", body: "" };
    const start = el.selectionStart ?? tpl.body.length;
    const end = el.selectionEnd ?? tpl.body.length;
    const sel = tpl.body.slice(start, end) || "texte";
    const close = closeMarker ?? marker;
    setMailTpl(key, "body", `${tpl.body.slice(0, start)}${marker}${sel}${close}${tpl.body.slice(end)}`);
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + marker.length, start + marker.length + sel.length);
    });
  };

  const [providerVariant, setProviderVariant] = useState("");

  const [previewing, setPreviewing] = useState(null);
  const preview = async (key, tplKey = key) => {
    setPreviewing(key);
    try {
      const tpl = (settings.mail_templates || {})[tplKey] || { subject: "", body: "" };
      const { data } = await api.post("/admin/emails/preview", { template: key, subject: tpl.subject, body: tpl.body });
      toast.success(`Aperçu envoyé à ${data.to}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setPreviewing(null);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Réglages renvoyés à l'identique : seuls les champs emails sont modifiés ici
      await api.patch("/admin/settings", {
        building_name: settings.building_name,
        building_address: settings.building_address,
        addresses: settings.addresses,
        notify_email: settings.notify_email || null,
        concierge_copy: settings.concierge_copy || "all",
        feature_cleaning: settings.feature_cleaning !== false,
        feature_ratings: settings.feature_ratings !== false,
        sub_buildings: settings.sub_buildings,
        garage_doors: settings.garage_doors,
        reminder_days: settings.reminder_days || 15,
        mail_templates: settings.mail_templates || {},
        mail_footer: settings.mail_footer || null,
        mail_header: settings.mail_header || null,
        mail_signature: settings.mail_signature || null,
        mail_sender_name: settings.mail_sender_name || null,
        mail_brand: settings.mail_brand || null,
        guardian_phone: settings.guardian_phone || null,
        logo_url: settings.logo_url || null,
      });
      toast.success("Configuration des emails enregistrée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  if (!settings) {
    return <div className="h-40 animate-pulse rounded-3xl bg-black/5" data-testid="emails-loading" />;
  }

  return (
    <form
      onSubmit={save}
      className="rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="admin-emails-tab"
    >
      <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
        <EnvelopeSimple size={20} weight="duotone" className="text-primary" /> Emails automatiques
      </h2>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Personnalisez l'objet et le message de chaque email. Laissez vide pour utiliser le texte standard. Cliquez sur une variable pour l'ajouter au message. La variable {"{lien}"} insère le bouton « Voir le signalement » à l'endroit voulu (sinon il est ajouté automatiquement à la fin), {"{urgence}"} place le bandeau rouge URGENCE où vous voulez. Mise en forme : sélectionnez du texte puis G (gras), I (italique), S (souligné), ou choisissez une taille et une couleur. {"{gestionnaire}"} insère les coordonnées du gestionnaire (onglet Contacts), {"{gestionnaire_nom}"} juste son nom.
      </p>
      <div className="mt-4 space-y-5" data-testid="settings-mail-templates">
        {MAIL_SECTIONS.map((sec) => {
          const effKey = sec.key === "provider" && providerVariant ? `provider_${providerVariant}` : sec.key;
          const tpl = (settings.mail_templates || {})[effKey] || { subject: "", body: "" };
          return (
            <div key={sec.key} className="rounded-2xl border border-black/5 bg-black/[0.015] p-4" data-testid={`mail-section-${sec.key}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold">{sec.title}</p>
                <button
                  type="button"
                  onClick={() => preview(sec.key, effKey)}
                  disabled={previewing === sec.key}
                  data-testid={`mail-${sec.key}-preview`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[11px] font-bold text-foreground/60 transition-colors hover:border-primary hover:text-primary disabled:opacity-50"
                >
                  {previewing === sec.key ? <SpinnerGap size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="bold" />}
                  M'envoyer un aperçu
                </button>
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{sec.hint}</p>
              {sec.key === "provider" && (
                <div className="mt-2">
                  <label htmlFor="mail-provider-variant" className="mb-1 block text-xs font-bold text-muted-foreground">Variante par rubrique</label>
                  <select
                    id="mail-provider-variant"
                    value={providerVariant}
                    onChange={(e) => setProviderVariant(e.target.value)}
                    data-testid="mail-provider-variant-select"
                    className={inputCls}
                  >
                    <option value="">Modèle par défaut (toutes les rubriques)</option>
                    {CATEGORIES.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                  {providerVariant && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Ce modèle remplace le modèle par défaut pour la rubrique « {CATEGORIES.find((c) => c.key === providerVariant)?.label} ». Laissez les champs vides pour hériter du modèle par défaut.
                    </p>
                  )}
                </div>
              )}
              <div className="mt-3">
                <label htmlFor={`mail-${effKey}-subject`} className="mb-1 block text-xs font-bold text-muted-foreground">Objet</label>
                <input
                  id={`mail-${effKey}-subject`}
                  value={tpl.subject}
                  onChange={(e) => setMailTpl(effKey, "subject", e.target.value)}
                  placeholder={sec.defaultSubject}
                  data-testid={`mail-${sec.key}-subject-input`}
                  className={inputCls}
                />
              </div>
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <label htmlFor={`mail-${effKey}-body`} className="block text-xs font-bold text-muted-foreground">Message</label>
                  <div className="flex flex-wrap items-center gap-1">
                    <button type="button" onClick={() => wrapSelection(effKey, "**")} title="Gras" aria-label="Gras" data-testid={`mail-${sec.key}-bold`} className="flex h-7 w-7 items-center justify-center rounded-lg border border-black/10 bg-white text-foreground/60 transition-colors hover:border-primary hover:text-primary">
                      <TextB size={13} weight="bold" />
                    </button>
                    <button type="button" onClick={() => wrapSelection(effKey, "*")} title="Italique" aria-label="Italique" data-testid={`mail-${sec.key}-italic`} className="flex h-7 w-7 items-center justify-center rounded-lg border border-black/10 bg-white text-foreground/60 transition-colors hover:border-primary hover:text-primary">
                      <TextItalic size={13} />
                    </button>
                    <button type="button" onClick={() => wrapSelection(effKey, "__")} title="Souligné" aria-label="Souligné" data-testid={`mail-${sec.key}-underline`} className="flex h-7 w-7 items-center justify-center rounded-lg border border-black/10 bg-white text-foreground/60 transition-colors hover:border-primary hover:text-primary">
                      <TextUnderline size={13} />
                    </button>
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) wrapSelection(effKey, `[taille=${e.target.value}]`, "[/taille]"); e.target.value = ""; }}
                      title="Taille du texte" aria-label="Taille du texte" data-testid={`mail-${sec.key}-size`}
                      className="h-7 rounded-lg border border-black/10 bg-white px-1.5 text-[11px] font-bold text-foreground/60"
                    >
                      <option value="">Taille</option>
                      <option value="14">Petit</option>
                      <option value="22">Normal</option>
                      <option value="28">Grand</option>
                      <option value="36">Très grand</option>
                    </select>
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) wrapSelection(effKey, `[couleur=${e.target.value}]`, "[/couleur]"); e.target.value = ""; }}
                      title="Couleur du texte" aria-label="Couleur du texte" data-testid={`mail-${sec.key}-color`}
                      className="h-7 rounded-lg border border-black/10 bg-white px-1.5 text-[11px] font-bold text-foreground/60"
                    >
                      <option value="">Couleur</option>
                      <option value="rouge">Rouge</option>
                      <option value="vert">Vert</option>
                      <option value="bleu">Bleu</option>
                      <option value="orange">Orange</option>
                      <option value="gris">Gris</option>
                      <option value="noir">Noir</option>
                    </select>
                  </div>
                </div>
                <textarea
                  id={`mail-${effKey}-body`}
                  rows={3}
                  value={tpl.body}
                  onChange={(e) => setMailTpl(effKey, "body", e.target.value)}
                  placeholder="Texte standard…"
                  data-testid={`mail-${sec.key}-body-input`}
                  className={cn(inputCls, "resize-none")}
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {sec.vars.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setMailTpl(effKey, "body", `${tpl.body}${tpl.body && !tpl.body.endsWith(" ") ? " " : ""}{${v}}`)}
                    data-testid={`mail-${sec.key}-var-${v}`}
                    className="rounded-full border border-black/10 bg-white px-2.5 py-1 font-mono text-[10px] font-bold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    {`{${v}}`}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 border-t border-black/10 pt-5">
        <label htmlFor="mail-signature" className="mb-1.5 block text-xs font-bold text-muted-foreground">
          Signature des emails
        </label>
        <textarea
          id="mail-signature"
          rows={3}
          value={settings.mail_signature || ""}
          onChange={(e) => setSettings({ ...settings, mail_signature: e.target.value })}
          placeholder={"Cordialement,\nNom Prénom — Fonction"}
          data-testid="mail-signature-input"
          className={cn(inputCls, "resize-none")}
        />
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          Ajoutée à la fin du message. Laissez vide pour ne rien afficher. Mise en forme possible : **gras**, *italique*, __souligné__.
        </p>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="mail-sender-name" className="mb-1.5 block text-xs font-bold text-muted-foreground">
            Nom d'expéditeur
          </label>
          <input
            id="mail-sender-name"
            value={settings.mail_sender_name || ""}
            onChange={(e) => setSettings({ ...settings, mail_sender_name: e.target.value })}
            placeholder="Ma Copropriété"
            data-testid="mail-sender-name-input"
            className={inputCls}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">Le nom affiché dans la boîte de réception (ex. « Syndic Immium »).</p>
        </div>
        <div>
          <label htmlFor="mail-brand" className="mb-1.5 block text-xs font-bold text-muted-foreground">
            Ligne sous l'email
          </label>
          <input
            id="mail-brand"
            value={settings.mail_brand || ""}
            onChange={(e) => setSettings({ ...settings, mail_brand: e.target.value })}
            placeholder="Ma Copropriété · le portail de votre copropriété"
            data-testid="mail-brand-input"
            className={inputCls}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">Remplace la ligne grise tout en bas des emails. Laissez vide pour le texte standard.</p>
        </div>
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid="emails-save-button"
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
      >
        {saving && <SpinnerGap size={16} className="animate-spin" />}
        Enregistrer les emails
      </button>
    </form>
  );
}
